.page {
  /* A tab body inside TitlesLayout → the gutter and background come from
     Layout's .main; this just lays out its own content. */
  width: 100%;
  /* Every bar on the leaderboard (MetricCell tracks + the revenue split bar)
     shares one height so the columns read as a set. */
  --metric-bar-height: 6px;
  overflow-x: clip;
}

/* ── Header: Define → Refine → meta ──────────────────────── */

.header {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin: var(--space-4) 0 var(--space-5) 0;
}

/* Row 1 — Define the universe (Platform / Region / Time) */
.defineRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

/* Grow to fit "Platform: Netflix +2" once set; 180px floor keeps the row even. */
.defineSelect {
  width: auto;
  min-width: 180px;
  max-width: 280px;
}

/* Portfolio picker sits inline right after the content-type control, as the
   "OR" alternative scope — it no longer floats to the end of the row. */
.portfolioPicker {
  flex: 0 0 auto;
}

/* "OR" separator between the content-type universe control and the portfolio
   picker: a muted, uppercase visual label that the two are alternatives. */
.orSep {
  align-self: center;
  margin: 0 var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-muted);
}

/* "Clear portfolio" action inside the disabled content-type control's tooltip.
   Danger-styled so it reads as the destructive escape hatch. */
.ctClearTip {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
}

.ctClearBtn {
  all: unset;
  cursor: pointer;
  align-self: stretch;
  text-align: center;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  color: #fff;
  background: var(--danger, #e5484d);
}

.ctClearBtn:hover {
  background: var(--danger-strong, #c93b40);
}

.ctClearBtn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--active-muted);
}

/* Row 2 — Refine (Genre primary + surfaced secondaries + More Filters) */
.refineRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.refineSelect {
  width: auto;
  min-width: 180px;
  max-width: 280px;
}

/* On/off filter chip — same box as the Select trigger (36px, input border,
   radius-md, text-sm), and the same gold "set" state when pressed. */
.toggleChip {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-muted);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}
.toggleChip:hover {
  border-color: var(--border-strong);
  color: var(--text-primary);
}
.toggleChip:focus-visible {
  border-color: var(--active);
  box-shadow: 0 0 0 3px var(--active-muted);
}
.toggleChipOn,
.toggleChipOn:hover {
  border-color: color-mix(in srgb, var(--active) 55%, var(--input-border));
  color: var(--text-primary);
}
.toggleChipOn .toggleChipIcon {
  color: var(--active);
}

/* Row 3 — count on the left, sort + columns on the right */
.metaRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.metaControls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.sortSelect {
  width: 170px;
}

/* Selection stated in words — the D360 summary-band idea */
.subtitle {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
}

.subtitle strong {
  color: var(--text-secondary);
  font-weight: var(--weight-semi);
}

/* A selected filter value in the scope sentence — gold, so every active choice
   reads as "you set this". Default (unselected) words stay muted via .subtitle. */
.scopeValue {
  color: var(--accent-secondary-text);
  font-weight: var(--weight-semi);
}

/* ── Table ───────────────────────────────────────────────── */

/* Frame around the shared LeaderboardTable. The @parrot/ui Table owns horizontal
   scroll (ScrollShadow) and its own radius, so this only supplies the card border
   + background that the current design sits in. */
.tableWrapper {
  width: 100%;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  overflow: hidden;
}

/* Search box hosted in the Title column header. Full-width within the cell,
   lowercase-styled to sit comfortably in the uppercase header row. */
.titleHead {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
}

.titleSearch {
  flex: 1;
  min-width: 0;
  max-width: 220px;
  text-transform: none;
  letter-spacing: normal;
  font-weight: var(--weight-regular);
}

/* ── Revenue cell with inline platform-split bar ──
   Same geometry as MetricCell (value, 5px gap, 3px full-width track) so the
   multi-platform rows sit on the same baseline as every other bar column. */
.revenueCell {
  display: block;
  width: 100%;
  min-width: 0;
}

/* The Tooltip trigger is inline-flex; stretch it to the cell. */
.revenueCell > span {
  display: flex;
  width: 100%;
}

.revenueInner {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  min-width: 0;
}

.revenueValue {
  font-size: var(--text-base);
  color: var(--text-primary);
  font-weight: var(--weight-semi);
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Brighten the platform split on hover, as the cell's affordance for its tooltip */
.splitBar {
  transition: filter var(--duration-fast) var(--ease-default);
}

.revenueCell:hover .splitBar {
  filter: brightness(1.15);
}

.rank {
  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: var(--weight-semi);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

/* Demand tiers use the demand-* scale, which exists for exactly this ranking */
.tier {
  display: inline-block;
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--text-secondary);
}

.tierExceptional {
  color: var(--demand-1);
}
.tierOutstanding {
  color: var(--demand-2);
}
.tierGood {
  color: var(--demand-3);
}
.tierAverage {
  color: var(--demand-4);
}
.tierBelowavg,
.tierBelowaverage {
  color: var(--demand-5);
}

.exclusiveTag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 var(--space-2);
  border: 1px solid var(--accent-secondary);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--accent-secondary-text);
}

/* ── Title cell ──────────────────────────────────────────── */

/* ── Platform cell ───────────────────────────────────────── */

.platformCell {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.platformName {
  font-size: var(--text-base);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.platformCellMulti {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.platformCount {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  font-weight: var(--weight-semi);
  white-space: nowrap;
}
.platformCountRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* Top platforms by revenue, overlapped like an avatar stack */
.platformIcons {
  display: inline-flex;
  flex-shrink: 0;
}
.platformIcons > * + * {
  margin-left: -4px;
}
.platformIcons > * {
  border-radius: var(--radius-sm);
  box-shadow: 0 0 0 1.5px var(--bg-card);
}

.platformMore {
  color: var(--text-muted);
  font-weight: var(--weight-semi);
}

.platformNames {
  font-size: var(--text-base);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* Inline so the cell's own alignment applies — this appears in both
   left- and right-aligned columns now. */
.noValue {
  color: var(--text-disabled);
}

/* Infinite-scroll sentinel, sits just below the last row */
.sentinel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.sentinelText {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.notice {
  padding: var(--space-4);
  background: var(--color-danger-muted);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  color: var(--text-primary);
}

.empty {
  padding: var(--space-12) var(--space-6);
  text-align: center;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.emptyTitle {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semi);
  color: var(--text-primary);
}

.emptyBody {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
}

@media (max-width: 640px) {
  .page {
    padding: var(--space-4) 0;
  }
}
